import { Injectable } from '@angular/core';
import { type OptimizelyUserContext, createInstance, type Client, setLogLevel } from '@optimizely/optimizely-sdk';

import { environment } from '../../../../environments/environment';
import { ToastersService } from '../toasters.service';
import { AuthService } from '../auth.service';
import { HelperFunctions } from '../../../utils/helper.functions';
import { HostLocationService } from '../hostLocation.service';
import { TenantType } from '../../enums/TenantType';
import { PERMISSION_TYPE } from '../../enums/PermissionType';
import { PermissionService } from '../permission.service';

const upsLocalStorageKeyPrefix = 'optimizely-user-profile-map-';

function parseQueryParamValue(value: string) {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagServiceV2 {
  private optimizelyClient: Client;
  private user: OptimizelyUserContext;
  private featureFlagOverrides: Record<string, Record<string, string | boolean>> = {};
  private hasShownAdBlockerToast = false;

  constructor(
    private readonly toasters: ToastersService,
    private readonly authService: AuthService,
    private readonly helpers: HelperFunctions,
    private readonly hostLocationService: HostLocationService,
    private readonly permissionService: PermissionService,
  ) {
    const queryParams = new URLSearchParams(window.location.search);

    queryParams.forEach((paramValue, paramKey) => {
      const [flag, variable] = paramKey.split('.');

      if (flag) {
        if (!this.featureFlagOverrides[flag]) {
          this.featureFlagOverrides[flag] = {};
        }

        if (variable) {
          this.featureFlagOverrides[flag][variable] = parseQueryParamValue(paramValue);
        } else {
          this.featureFlagOverrides[flag].enabled = parseQueryParamValue(paramValue);
        }
      }
    });
  }

  async init(): Promise<void> {
    setLogLevel('error');
  
    this.optimizelyClient = this.createOptimizelyClient();
  
    if (!this.optimizelyClient) {
      throw new Error('Optimizely client failed to initialize');
    }
  
    try {
      const { success, reason } = await this.optimizelyClient.onReady();
  
      if (!success) {
        throw new Error(`Optimizely failed to initialize: ${reason}`);
      }
  
      this.createUserContext();
    } catch (error) {
      console.error('Optimizely initialization error:', error);
      this.handleOptimizelyError(error);
    }
  }

  private createOptimizelyClient() {
    return createInstance({
      sdkKey: environment.optimizely.sdkKey,
      datafileOptions: {
        autoUpdate: true,
        updateInterval: 60000,
      },
      errorHandler: {
        handleError: (error) => this.handleOptimizelyError(error),
      },
      userProfileService: {
        lookup: (userId) => {
          const profileMap = localStorage.getItem(`${upsLocalStorageKeyPrefix}-${userId}`);
          return profileMap ? JSON.parse(profileMap) : {};
        },
        save: (userProfileMap) => {
          localStorage.setItem(`${upsLocalStorageKeyPrefix}-${userProfileMap.user_id}`, JSON.stringify(userProfileMap));
        },
      },
    });
  }

  private handleOptimizelyError(error: unknown): void {
    console.error('Optimizely error:', error);
    const isPossiblyAdBlocker = this.isAdBlockerRelatedError(error);
  
    if (isPossiblyAdBlocker && !this.hasShownAdBlockerToast) {
      this.hasShownAdBlockerToast = true;
  
      this.toasters.createNewToast({
        type: 'Error',
        isStatusToast: true,
        title: 'Please disable your ad-blocker',
      });
    }
  
    // TODO: report to Sentry with detailed context
  }

  private isAdBlockerRelatedError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return false;
  
    const err = error as Error;
  
    return (
      err.message?.toLowerCase().includes('failed to fetch') &&
      err.stack?.toLowerCase().includes('optimizely')
    );
  }

  getAvailableScreensCount() {
    const availableScreens = [
      this.getValue('haulierVerificationProcess', 'fleetDetails') as boolean,
      this.getValue('haulierVerificationProcess', 'documents') as boolean,
      this.getValue('haulierVerificationProcess', 'workPreferences') as boolean,
      this.getValue('haulierVerificationProcess', 'paymentDetails') as boolean,
    ];
    return availableScreens.filter((screen) => screen).length;
  }

  isEnabled(flag: string) {
    if (typeof this.featureFlagOverrides[flag]?.enabled === 'boolean') {
      return this.featureFlagOverrides[flag].enabled as boolean;
    }

    return this.user.decide(flag, []).enabled;
  }

  getValue(flag: string, variable: string) {
    if (typeof this.featureFlagOverrides[flag]?.[variable] !== 'undefined') {
      return this.featureFlagOverrides[flag][variable];
    }

    return this.user.decide(flag, []).variables[variable];
  }

  private createUserContext() {
    const user = this.authService.getUser();
    const tenant = this.authService.getTenant();
    const host = this.hostLocationService.getHost();
    const platform = this.helpers.getEnvironmentAndProject(host).project;

    this.user = this.optimizelyClient.createUserContext(user?.id || 'anonymous', {
      tenantId: tenant?.id,
      tenantName: tenant?.name,
      email: user?.email,
      subcon: this.permissionService.hasPermission(PERMISSION_TYPE.MODULE.SHIPPER_CONTRACTOR.toString()),
      shipper: tenant?.type === TenantType.shipper,
      haulier: tenant?.type === TenantType.haulier,
      superadmin: this.authService.isSuperAdmin(),
      platform,
    });
  }
}
